import { Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { TranslateCacheService } from 'ngx-translate-cache';
import { environment } from '../../environments/environment';
import { AppUtils } from '../utils/app.utils';
import { LocalStorageUtils } from '../utils/localStorage.utils';
import { Observable } from 'rxjs';
import { ILanguage, IStore } from '../models/settings.model';

@Injectable({
  providedIn: 'root',
})
export class I18nService {
  static readonly languageParamRegex: RegExp = /^[a-z]{2}-[a-z]{2,3}$/;
  static readonly languageUrlRegex: RegExp = /^\/[a-z]{2}-[a-z]{2,3}\//;

  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly translateCacheService: TranslateCacheService = inject(TranslateCacheService);

  getTranslationByKey(key: string | Array<string>, params?: any): Observable<any> {
    return this.translateService.get(key, params);
  }

  validateLanguage(language: string): boolean {
    return this.translateService.langs.includes(language);
  }

  getDefaultLanguage(): string {
    return environment.defaultLang;
  }

  getCachedLanguage(): string {
    return this.translateCacheService.getCachedLanguage();
  }

  getCurrentLanguage(): string {
    const [defaultLangCode] = this.getDefaultLanguage().split('-');
    const [storageLangCode] = LocalStorageUtils.getKeyValue('lang').split('-');

    return storageLangCode || defaultLangCode;
  }

  getShopAllLanguages(): Array<string> {
    const allLanguages: Array<string>[] = environment.stores.map(store => store.languages.map(lang => lang.id));

    return allLanguages.length === 0 ? [] : allLanguages.flat().sort((a, b) => a.localeCompare(b));
  }

  getCurrentLocale(): any {
    const currentStore: IStore = AppUtils.getCurrentStore();
    const currentLanguage: string = this.getCurrentLanguage();

    const language: ILanguage = currentStore.languages.find(lang => lang.code === currentLanguage)
      ?? currentStore.languages[0];

    return this.buildLangParam(currentStore.marketCode, language.code);
  }

  getCurrencyLocale(): string | null {
    const currentStore: IStore = AppUtils.getCurrentStore();
    const currentLanguage: string = this.getCurrentLanguage();

    const language: ILanguage = currentStore.languages.find(lang => lang.code === currentLanguage)
      ?? currentStore.languages[0];

    return language ? this.buildLangParam(currentStore.marketCode, language.defaultCurrencyLocale) : null;
  }

  getStoreByLocale(locale: string): IStore | null {
    const [_, marketCode] = locale.split('-');
    const currentStore: IStore | undefined = environment.stores.find(store => store.marketCode === marketCode);

    return currentStore || null;
  }

  getShopAllMarkets(): Array<string> {
    const marketCodes: string[] = environment.stores.map(store => store.marketCode);

    return marketCodes.length === 0 ? [] : marketCodes;
  }

  validateMarket(marketCode: string): boolean {
    const marketCodes: string[] = this.getShopAllMarkets();
    return marketCodes.includes(marketCode);
  }

  getCurrentParam(): string {
    return this.buildLangParam(AppUtils.getCurrentMarket(), this.getCurrentLanguage());
  }

  buildLangParam(marketCode: string, language: string): string {
    return `${language}-${marketCode}`;
  }

  validateLangParam(param: Params): boolean {
    return 'lang' in param && param.lang.length > 0 && param.lang.includes('-');
  }

  getLangParamData(langParam: string): string[] {
    return langParam.split('-');
  }
}
